import React, { useState, useEffect } from "react";
import { CancelButton, FormButton } from "../../../components/Botton";
// import { CancelButton, FormButton } from "../../../../components/Botton";

interface BulkEditModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: BulkEditData) => void;
    selectedCount: number;
    // Dropdown data
    standards: any[];
    courses: any[];
    subjects: any[];
    languages: any[];
    // For cascading dropdowns
    onStandardChange: (standard: string) => void;
    onCourseChange: (course: string) => void;
    filteredCourses: any[];
    filteredSubjects: any[];
}

export interface BulkEditData {
    level?: string;
    subject?: string;
    standard?: string;
    courseName?: string;
    languageName?: string;
    isLiveMcq?: boolean;
}

const BulkEditModal: React.FC<BulkEditModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    selectedCount,
    standards,
    courses,
    subjects,
    languages,
    onStandardChange,
    onCourseChange,
    filteredCourses,
    filteredSubjects,
}) => {
    const [formData, setFormData] = useState<BulkEditData>({
        level: "",
        subject: "",
        standard: "",
        courseName: "",
        languageName: "",
        isLiveMcq: undefined,
    });

    // Reset form when modal closes
    useEffect(() => {
        if (!isOpen) {
            setFormData({
                level: "",
                subject: "",
                standard: "",
                courseName: "",
                languageName: "",
                isLiveMcq: undefined,
            });
        }
    }, [isOpen]);

    const handleChange = (field: keyof BulkEditData, value: any) => {
        setFormData((prev) => ({ ...prev, [field]: value }));

        // Handle cascading dropdowns
        if (field === "standard") {
            onStandardChange(value);
            setFormData((prev) => ({ ...prev, courseName: "", subject: "" }));
        } else if (field === "courseName") {
            onCourseChange(value);
            setFormData((prev) => ({ ...prev, subject: "" }));
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        // Filter out empty values
        const dataToSubmit: BulkEditData = {};
        Object.entries(formData).forEach(([key, value]) => {
            if (value !== "" && value !== undefined) {
                dataToSubmit[key as keyof BulkEditData] = value;
            }
        });

        // Check if at least one field is selected
        if (Object.keys(dataToSubmit).length === 0) {
            alert("Please select at least one field to update");
            return;
        }

        onSubmit(dataToSubmit);
    };

    if (!isOpen) return null;

    return (
        <div className="flex flex-col items-center w-full h-screen p-8">
            <div className="bg-white shadow-md rounded-lg pt-6 pb-6 w-full">
                <div className="border-b-2 w-[100%]">
                    <h1 className="text-3xl mb-2 text-gray-700 ml-5">
                        Bulk Edit Questions
                    </h1>
                    <p className="text-sm text-gray-500 ml-5 mb-2">
                        Editing {selectedCount} question{selectedCount !== 1 ? "s" : ""}
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6 p-6">
                    <fieldset className="border p-4 rounded mb-4 bg-white">
                        <legend className="text-xl mb-4 font-semibold text-gray-700">
                            Update Fields
                        </legend>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Difficulty Level */}
                            <div>
                                <label className="block text-gray-700 text-base font-semibold mb-2">
                                    Difficulty Level:
                                </label>
                                <select
                                    value={formData.level || ""}
                                    onChange={(e) => handleChange("level", e.target.value)}
                                    className="appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline"
                                >
                                    <option value="">No Change</option>
                                    <option value="Easy">Easy</option>
                                    <option value="Medium">Medium</option>
                                    <option value="Hard">Hard</option>
                                </select>
                            </div>

                            {/* Education/Standard */}
                            <div>
                                <label className="block text-gray-700 text-base font-semibold mb-2">
                                    Education:
                                </label>
                                <select
                                    value={formData.standard || ""}
                                    onChange={(e) => handleChange("standard", e.target.value)}
                                    className="appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline"
                                >
                                    <option value="">No Change</option>
                                    {standards.map((std) => (
                                        <option key={std._id} value={std.standard}>
                                            {std.standard}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Stream */}
                            <div>
                                <label className="block text-gray-700 text-base font-semibold mb-2">
                                    Stream:
                                </label>
                                <select
                                    value={formData.courseName || ""}
                                    onChange={(e) => handleChange("courseName", e.target.value)}
                                    disabled={!formData.standard}
                                    className={`appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline ${!formData.standard ? "bg-gray-100 cursor-not-allowed" : ""
                                        }`}
                                >
                                    <option value="">No Change</option>
                                    {filteredCourses.map((course) => (
                                        <option key={course._id} value={course.courseName}>
                                            {course.courseName}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Subject */}
                            <div>
                                <label className="block text-gray-700 text-base font-semibold mb-2">
                                    Subject:
                                </label>
                                <select
                                    value={formData.subject || ""}
                                    onChange={(e) => handleChange("subject", e.target.value)}
                                    disabled={!formData.standard || !formData.courseName}
                                    className={`appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline ${!formData.standard || !formData.courseName
                                        ? "bg-gray-100 cursor-not-allowed"
                                        : ""
                                        }`}
                                >
                                    <option value="">No Change</option>
                                    {filteredSubjects.map((subject) => (
                                        <option key={subject._id} value={subject.subject}>
                                            {subject.subject}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Language */}
                            <div>
                                <label className="block text-gray-700 text-base font-semibold mb-2">
                                    Language Name:
                                </label>
                                <select
                                    value={formData.languageName || ""}
                                    onChange={(e) => handleChange("languageName", e.target.value)}
                                    className="appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline"
                                >
                                    <option value="">No Change</option>
                                    {languages.map((language) => (
                                        <option key={language._id} value={language.languageName}>
                                            {language.languageName}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Is Live MCQ */}
                            <div>
                                <label className="block text-gray-700 text-base font-semibold mb-2">
                                    Also add as live MCQ ?:
                                </label>
                                <div className="flex items-center gap-6">
                                    {/* Three-state switch */}
                                    <div className="flex items-center gap-3">
                                        <span className={`text-sm font-semibold ${formData.isLiveMcq === false ? 'text-red-600' : 'text-gray-400'}`}>
                                            Not Live
                                        </span>
                                        <div
                                            className="relative inline-flex items-center cursor-pointer"
                                            onClick={() => {
                                                // Cycle through states: undefined -> true -> false -> undefined
                                                if (formData.isLiveMcq === undefined) {
                                                    handleChange("isLiveMcq", true);
                                                } else if (formData.isLiveMcq === true) {
                                                    handleChange("isLiveMcq", false);
                                                } else {
                                                    handleChange("isLiveMcq", undefined);
                                                }
                                            }}
                                        >
                                            <div className={`w-20 h-10 rounded-full transition-colors duration-300 ${formData.isLiveMcq === true
                                                    ? 'bg-green-500'
                                                    : formData.isLiveMcq === false
                                                        ? 'bg-red-500'
                                                        : 'bg-gray-300'
                                                }`}>
                                                <div className={`absolute top-1 left-1 w-8 h-8 bg-white rounded-full shadow-md transition-transform duration-300 flex items-center justify-center ${formData.isLiveMcq === true
                                                        ? 'transform translate-x-10'
                                                        : formData.isLiveMcq === false
                                                            ? 'transform translate-x-0'
                                                            : 'transform translate-x-5'
                                                    }`}>
                                                    {formData.isLiveMcq === true && (
                                                        <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                        </svg>
                                                    )}
                                                    {formData.isLiveMcq === false && (
                                                        <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                        </svg>
                                                    )}
                                                    {formData.isLiveMcq === undefined && (
                                                        <span className="text-gray-500 text-xs font-bold">-</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <span className={`text-sm font-semibold ${formData.isLiveMcq === true ? 'text-green-600' : 'text-gray-400'}`}>
                                            Live
                                        </span>
                                    </div>
                                    <span className={`text-sm ${formData.isLiveMcq === undefined ? 'text-gray-600 font-semibold' : 'text-gray-400'}`}>
                                        {formData.isLiveMcq === undefined ? '(No Change)' : ''}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </fieldset>

                    <div className="flex justify-start mt-6 ml-10">
                        <FormButton className="py-2 px-4 rounded mr-4" type="submit">
                            UPDATE
                        </FormButton>
                        <CancelButton
                            className="py-2 px-4 rounded"
                            onClick={onClose}
                            type="button"
                        >
                            Cancel
                        </CancelButton>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default BulkEditModal;
